
import { useState, FC, useEffect, useRef } from "react";
import styles from "../ContactForm.module.css";
import { obj } from "types";
import Modal from "react-modal";
import { countries, markets } from "../inputOptions";
import $ from "jquery";
import classNames from "classnames";
import ClipLoader from "react-spinners/ClipLoader";
import { AnimatePresence, motion } from "framer-motion";
import { ExitBtn } from "components/atoms";
import { TextEditor } from "components/molecules";
import { ReactModalStyles } from "components/constants";
import { useDeviceClassification } from "utils";

type ContactUsFormModalProps = {
    isOpen: boolean;
    handleIsOpenChange: (bool: boolean) => void;
    data: {
        SuccessTitle: string,
        SuccessDescription: string
    }
}

type TFormFields = {
    name: string,
    label: string,
    colWidth: string,
    type: string,
    required?: boolean,
    json?: any,
    height: string;
    placeholder?: string
}

const formFields: TFormFields[] = [
    { name: "1", label: "Your Email", colWidth: "span-12", type: "email", required: true, height: '54px' },
    { name: "contactus_firstname", label: "First Name", colWidth: "span-6", type: "text", required: true, height: '54px' },
    { name: "contactus_lastname", label: "Last Name", colWidth: "span-6", type: "text", required: true, height: '54px' },
    { name: "contactus_org", label: "Organisation Name", colWidth: "span-6", type: "text", required: true, height: '54px' },
    { name: "contactus_position", label: "Position Title", colWidth: "span-6", type: "text", required: false, height: '54px' },
    { name: "Country", label: "Country", colWidth: "span-6", type: "select", required: true, json: countries, height: '54px' },
    { name: "contactus_market", label: "Market", colWidth: "span-6", type: "select", required: false, json: markets, height: '54px' },
    { name: "free-field-context", label: "Tell us a little bit about your challenge?", colWidth: "span-12", type: "text-area", required: true, height: '102px', placeholder: "Give us some insight into what expertise or solutions you are looking for..." },
    { name: "contactus_optin", label: "I would like to to receive communications from Aurecon in accordance with Aurecon’s Privacy Policy. I understand I can opt-out at any time.", colWidth: "span-8", type: "checkbox", required: true, height: 'auto' }
];

export const ContactUsFormModal: FC<ContactUsFormModalProps> = ({ isOpen, handleIsOpenChange, data }) => {
    const [formData, setFormData] = useState<obj>({
        PageNameURL: window.location.href
    });
    const formRef = useRef(null);
    const [userIsSubscribed, setUserIsSubscribed] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { isDesktop } = useDeviceClassification();


    // handle document scrolling on resective Modal states
    useEffect(() => {
        isOpen && $('html').css({ overflow: 'hidden' });
        !isOpen && $('html').css({ overflow: 'scroll' });
        !isOpen && setErrorMessage(null);

        /***
         * 1. Remove asterics from un-required fields
         * 2. Add asterics to required select fields
         **/
        if (isOpen) {
            setTimeout(() => {
                formFields.forEach(f => {
                    if (!f.required) {
                        const el = document.querySelector(`${f.type === 'select' ? 'select' : 'input'}[name="${f.name}"]`);
                        const sibling = el?.previousElementSibling;
                        sibling.innerHTML = f.label.toUpperCase();
                    }
                    if (f.type === 'select' && f.required) {
                        const el = document.querySelector(`${f.type === 'select' ? 'select' : 'input'}[name="${f.name}"]`);
                        const sibling = el?.previousElementSibling;
                        sibling.innerHTML = f.label.toUpperCase() + '*';
                    }
                })
            }, 100)
        }


    }, [isOpen]);

    const defaultErrorMessage =
        "Oops, something went wrong! Please make sure your browser is not blocking form submission.";

    const handleDataChange = (label: string, value: string | number) => {
        setFormData({ ...formData, [label]: value })
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);


        const data = new FormData(formRef.current as HTMLFormElement);
        await fetch('https://analytics-au.clickdimensions.com/forms/h/a6nLbs8TVk0KykcdW2zKR5', {
            body: data,
            method: "POST",
            mode: "cors"
        }).then((res) => {
            if (res.status === 200 && res.redirected) {
                setErrorMessage(null);
                setUserIsSubscribed(true);
            } else {
                const errorUrl = new URL(res.url);
                const message = errorUrl?.searchParams?.get("errorMessage");
                setErrorMessage(
                    typeof message === "string" && message.length > 0 ? `Error: ${message}` : defaultErrorMessage
                );
            }
        })
            .catch(() => setErrorMessage(defaultErrorMessage))
            .finally(() => setIsLoading(false));
    };

    return (
        <Modal
            style={{...ReactModalStyles}}
            isOpen={isOpen}
            className={classNames("Modal", { success: userIsSubscribed, error: errorMessage })}
            onRequestClose={() => handleIsOpenChange(false)}
            shouldCloseOnOverlayClick={true}
        >

            <div className={classNames(styles.modalWrapper, { success: userIsSubscribed })}>
                <ExitBtn
                    style={{ top: '20px', right: '20px' }}
                    userIsSubscribed={userIsSubscribed}
                    handleIsOpenChange={(bool: boolean) => handleIsOpenChange(bool)}
                />

                {errorMessage &&
                    <AnimatePresence>
                        <motion.div
                            initial={{ opacity: 0, top: -50 }}
                            animate={{ opacity: 1, top: 0 }}
                            exit={{ opacity: 0, top: -50 }}
                            className={styles.errorMsgWrapper}>
                            <p>
                                {errorMessage}
                            </p>
                        </motion.div>
                    </AnimatePresence>
                }

                {userIsSubscribed ?
                    <AnimatePresence>
                        <motion.div
                            initial={{ opacity: 0, top: -50 }}
                            animate={{ opacity: 1, top: 0 }}
                            exit={{ opacity: 0, top: -50 }}
                            className={styles.successMsgWrapper}>
                            <h5>{data.SuccessTitle}</h5>
                            <TextEditor data={data.SuccessDescription} />
                        </motion.div>
                    </AnimatePresence>
                    :
                    <>
                        {!errorMessage &&
                            <AnimatePresence>
                                <motion.form
                                    initial={{ opacity: 0, top: -50 }}
                                    animate={{ opacity: 1, top: 0 }}
                                    exit={{ opacity: 0, top: -50 }}
                                    ref={formRef}
                                    onSubmit={(e) => onSubmit(e)}
                                    acceptCharset="utf-8"
                                    encType="multipart/form-data"
                                >
                                    <h2 className={`${isDesktop ? 'span-12' : 'span-4'}`}>We just need a few details...</h2>

                                    <input type="hidden" name="PageNameURL" value={window.location.href} />
                                    {formFields.map((field) => {
                                        if (field.type === "select") {
                                            return <div key={`input-${field.name}`} className={isDesktop ? field.colWidth : 'span-4'}>
                                                <label htmlFor={field.name}> <span className="text-form">{field.label.toUpperCase()}</span>
                                                    <select
                                                        style={{ height: field.height }}
                                                        required={field.required}
                                                        placeholder="Please select"
                                                        onChange={(e) => handleDataChange(field.name, e.target.value)}
                                                        id={field.name}
                                                        key={field.name}
                                                        name={field.name}
                                                        aria-label={field.name}
                                                        aria-required="true"
                                                        defaultValue={""}
                                                        aria-invalid="false">
                                                        <option key={field.label + 'disabled-option'} value="" disabled>Please select</option>
                                                        {field.json.map((v, i) => (
                                                            <option key={v.value + i} value={v.value}>{v.name}</option>
                                                        ))}
                                                    </select>
                                                </label>
                                            </div>
                                        } else if (field.type === "checkbox") {
                                            return <div key={`input-${field.name}`} className={`${isDesktop ? field.colWidth : 'span-4'} checkbox-wrapper`}>
                                                <label style={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'flex-start' }}>
                                                    <p className="text-form checkbox-text">{field.label}</p>
                                                    <input
                                                        style={{ height: field.height }}
                                                        onChange={(e) => handleDataChange(field.name, e.target.value)}
                                                        name={field.name}
                                                        type={field.type}
                                                        required={field.required}
                                                    />
                                                </label>
                                            </div>
                                        } else if (field.type === "text-area") {
                                            return <div key={`input-${field.name}`} className={isDesktop ? field.colWidth : 'span-4'}>
                                                <label> <span className="text-form">{field.label.toUpperCase()}*</span>
                                                    <textarea
                                                        placeholder={field.placeholder ? field.placeholder : null}
                                                        style={{ height: field.height }}
                                                        onChange={(e) => handleDataChange(field.name, e.target.value)}
                                                        name={field.name}
                                                        required={field.required}
                                                    /></label>
                                            </div>
                                        } else {
                                            return <div key={`input-${field.name}`} className={isDesktop ? field.colWidth : 'span-4'}>
                                                <label> <span className="text-form">{field.label.toUpperCase()}*</span>
                                                    <input
                                                        placeholder={field.placeholder ? field.placeholder : null}
                                                        style={{ height: field.height }}
                                                        onChange={(e) => handleDataChange(field.name, e.target.value)}
                                                        name={field.name}
                                                        type={field.type}
                                                        required={field.required}
                                                    /></label>
                                            </div>
                                        }
                                    }
                                    )}

                                    <div className={`${isDesktop ? 'span-4' : 'span-4' }`}>
                                        <div className={styles.btnWrapper}>
                                            <button
                                                className="btn btn-g-full"
                                                type="submit"
                                                value="Send">Send {isLoading && <ClipLoader />}</button>
                                        </div>
                                    </div>

                                    <div className={`${isDesktop ? 'span-12' : 'span-4'}`}>
                                        {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
                                    </div>
                                </motion.form>
                            </AnimatePresence>
                        }
                    </>

                }
            </div>
        </Modal>

    )
}